import { BUZON_CASOS } from '../../../lib/modulos';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  CloudCheck, CloudOff, Copy, Eye, FolderOpen, Globe, Link2, Loader2, Mail, Moon, MoreVertical, Palette, Printer,
  Redo2, Send, Settings2, Star, Sun, Trash2, Undo2, UserPlus,
} from 'lucide-react';
import { api } from '../../../lib/api';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../../ui/dropdown-menu';
import { AuroraLogo, OFFICE_APPS, ProductIcon } from '../../office/icons';

const COLORES_AVATAR = ['#2563eb', '#3f9576', '#b07d2e', '#4b7bc0', '#c05c6a', '#7a68d4', '#2f9170', '#9c6ac8'];

export function iniciales(nombre = '') {
  const partes = nombre.replace(/[^\p{L}\s]/gu, ' ').trim().split(/\s+/).filter(Boolean);
  return ((partes[0]?.[0] || '') + (partes[1]?.[0] || '')).toUpperCase() || '?';
}

export function colorAvatar(semilla = '') {
  let h = 0;
  for (let i = 0; i < semilla.length; i += 1) h = (h * 31 + semilla.charCodeAt(i)) >>> 0;
  return COLORES_AVATAR[h % COLORES_AVATAR.length];
}

const PESTANAS = [
  { id: 'preguntas', texto: 'Preguntas' },
  { id: 'respuestas', texto: 'Respuestas' },
  { id: 'casos', texto: 'Casos' }, // buzón de casos (components/forms/buzon)
  { id: 'configuracion', texto: 'Configuración' },
];

function EstadoGuardado({ guardado, compacto }) {
  if (guardado === 'reintentando') {
    return (
      <span className="fe-guardado is-error" role="status" title="Sin conexión, reintentando">
        <CloudOff /> {!compacto && <span>Sin conexión, reintentando</span>}
      </span>
    );
  }
  if (guardado === 'guardando' || guardado === 'pendiente') {
    return (
      <span className="fe-guardado" role="status" title="Guardando…">
        <Loader2 className="od-spin" /> {!compacto && <span>Guardando…</span>}
      </span>
    );
  }
  return (
    <span className="fe-guardado" role="status" title="Todos los cambios se guardaron en Drive">
      <CloudCheck /> {!compacto && <span>Todos los cambios se guardaron en Drive</span>}
    </span>
  );
}

/** Cabecera del editor (nombre, guardado, presencia y acciones) y pestañas Preguntas / Respuestas / Configuración. */
export default function EncabezadoEditor({
  archivo, formulario, puedeEditar, guardado, presencia, usuario, oscuro, movil,
  pestana, onPestana, acciones, puedeDeshacer, puedeRehacer, auroraAbierta,
}) {
  const [nombre, setNombre] = useState(archivo?.name || '');
  const [destacado, setDestacado] = useState(!!archivo?.starred);
  const pub = formulario?.publicacion || {};

  useEffect(() => setNombre(archivo?.name || ''), [archivo?.name]);
  useEffect(() => setDestacado(!!archivo?.starred), [archivo?.starred]);

  const confirmarNombre = () => {
    const limpio = nombre.trim();
    if (!limpio) {
      setNombre(archivo?.name || '');
      return;
    }
    if (limpio !== archivo?.name) acciones.onNombre(limpio);
  };

  const alternarDestacado = async () => {
    const nuevo = !destacado;
    setDestacado(nuevo);
    try {
      await api.post(`/drive/files/${archivo.id}/star`, null, { params: { starred: nuevo } });
    } catch {
      setDestacado(!nuevo);
      toast.error('No se pudo actualizar');
    }
  };

  const copiarEnlace = async () => {
    if (!pub.url_publica) return;
    try {
      await navigator.clipboard.writeText(pub.url_publica);
      toast.success('Enlace para responder copiado');
    } catch {
      window.prompt('Copia el enlace:', pub.url_publica);
    }
  };

  const nombreUsuario = usuario?.name || usuario?.email || '';
  const otros = (presencia || []).filter((p) => p.id !== usuario?.id && p.correo !== usuario?.email);
  const visibles = otros.slice(0, movil ? 2 : 4);
  const respuestas = formulario?.respuestas_total || 0;

  const menuMas = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="fe-icono-btn fe-icono-lg" aria-label="Más acciones" title="Más">
          <MoreVertical />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="fe-menu w-64">
        {movil && (
          <>
            <DropdownMenuItem onSelect={acciones.onVistaPrevia}><Eye /> Vista previa</DropdownMenuItem>
            {puedeEditar && <DropdownMenuItem onSelect={acciones.onTema}><Palette /> Personalizar tema</DropdownMenuItem>}
            {puedeEditar && <DropdownMenuItem onSelect={acciones.onDeshacer} disabled={!puedeDeshacer}><Undo2 /> Deshacer</DropdownMenuItem>}
            {puedeEditar && <DropdownMenuItem onSelect={acciones.onRehacer} disabled={!puedeRehacer}><Redo2 /> Rehacer</DropdownMenuItem>}
            {puedeEditar && <DropdownMenuItem onSelect={acciones.onEnviar}><Send /> Enviar</DropdownMenuItem>}
            {pub.publicado && pub.url_publica && <DropdownMenuItem onSelect={copiarEnlace}><Link2 /> Copiar enlace para responder</DropdownMenuItem>}
            <DropdownMenuItem onSelect={acciones.onAlternarOscuro}>{oscuro ? <Sun /> : <Moon />} {oscuro ? 'Tema claro' : 'Tema oscuro'}</DropdownMenuItem>
            {!destacado ? (
              <DropdownMenuItem onSelect={alternarDestacado}><Star /> Destacar</DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={alternarDestacado}><Star fill="currentColor" /> Quitar de destacados</DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
          </>
        )}
        {movil && (
          <DropdownMenuItem onSelect={acciones.onAurora}>
            <AuroraLogo size={16} /> Aurora IA
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onSelect={acciones.onCopiar}><Copy /> Hacer una copia</DropdownMenuItem>
        {puedeEditar && <DropdownMenuItem onSelect={acciones.onPrecompletar}><Link2 /> Obtener enlace precompletado</DropdownMenuItem>}
        <DropdownMenuItem onSelect={acciones.onImprimir}><Printer /> Imprimir</DropdownMenuItem>
        <DropdownMenuSeparator />
        {puedeEditar && <DropdownMenuItem onSelect={acciones.onColaboradores}><UserPlus /> Añadir colaboradores</DropdownMenuItem>}
        {puedeEditar && <DropdownMenuItem onSelect={acciones.onPreferencias}><Settings2 /> Preferencias</DropdownMenuItem>}
        <DropdownMenuItem onSelect={acciones.onDrive}><FolderOpen /> Abrir en Drive</DropdownMenuItem>
        {movil && <DropdownMenuItem onSelect={acciones.onCorreo}><Mail /> Ir al correo</DropdownMenuItem>}
        {acciones.onPapelera && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={acciones.onPapelera} className="fe-menu-peligro"><Trash2 /> Mover a la papelera</DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <header className="fe-cabecera">
      <div className="fe-cabecera-fila">
        <button type="button" className="fe-app-btn" onClick={acciones.onDrive} title={`${OFFICE_APPS.form.name} · volver a Drive`} aria-label="Volver a Drive">
          <ProductIcon kind="form" size={movil ? 30 : 36} />
        </button>
        <div className="fe-cabecera-titulo">
          <input
            className="fe-nombre"
            value={nombre}
            size={Math.max(8, Math.min(nombre.length + 1, 42))}
            disabled={!puedeEditar}
            aria-label="Nombre del formulario en Drive"
            onChange={(e) => setNombre(e.target.value)}
            onBlur={confirmarNombre}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
              if (e.key === 'Escape') {
                setNombre(archivo?.name || '');
                e.currentTarget.blur();
              }
            }}
          />
          {!movil && (
            <button
              type="button"
              className={`fe-icono-btn ${destacado ? 'is-destacado' : ''}`}
              onClick={alternarDestacado}
              title={destacado ? 'Quitar de destacados' : 'Destacar'}
              aria-label={destacado ? 'Quitar de destacados' : 'Destacar'}
              aria-pressed={destacado}
            >
              <Star fill={destacado ? 'currentColor' : 'none'} />
            </button>
          )}
          {puedeEditar && <EstadoGuardado guardado={guardado} compacto={movil} />}
        </div>

        <div className="fe-cabecera-acciones">
          {visibles.length > 0 && (
            <div className="fe-presencia" aria-label={`${otros.length} ${otros.length === 1 ? 'persona editando' : 'personas editando'}`}>
              {visibles.map((p) => (
                <span
                  key={p.id || p.correo}
                  className="fe-avatar fe-avatar-presencia"
                  style={{ background: p.color || colorAvatar(p.correo || p.nombre), outlineColor: p.color || colorAvatar(p.correo || p.nombre) }}
                  title={`${p.nombre || p.correo} está editando`}
                >
                  {iniciales(p.nombre || p.correo)}
                </span>
              ))}
              {otros.length > visibles.length && <span className="fe-avatar fe-avatar-mas">+{otros.length - visibles.length}</span>}
            </div>
          )}

          {!movil && (
            <>
              {puedeEditar && (
                <button type="button" className="fe-icono-btn fe-icono-lg" onClick={acciones.onTema} title="Personalizar tema" aria-label="Personalizar tema">
                  <Palette />
                </button>
              )}
              <button type="button" className="fe-icono-btn fe-icono-lg" onClick={acciones.onVistaPrevia} title="Vista previa" aria-label="Vista previa">
                <Eye />
              </button>
              {puedeEditar && (
                <>
                  <button type="button" className="fe-icono-btn fe-icono-lg" onClick={acciones.onDeshacer} disabled={!puedeDeshacer} title="Deshacer (Ctrl+Z)" aria-label="Deshacer">
                    <Undo2 />
                  </button>
                  <button type="button" className="fe-icono-btn fe-icono-lg" onClick={acciones.onRehacer} disabled={!puedeRehacer} title="Rehacer (Ctrl+Mayús+Z)" aria-label="Rehacer">
                    <Redo2 />
                  </button>
                </>
              )}
              {pub.publicado && pub.url_publica && (
                <button type="button" className="fe-icono-btn fe-icono-lg" onClick={copiarEnlace} title="Copiar enlace para responder" aria-label="Copiar enlace para responder">
                  <Link2 />
                </button>
              )}
              {puedeEditar && (
                <button type="button" className="fe-btn-borde fe-btn-cabecera" onClick={acciones.onEnviar}>
                  <Send /> Enviar
                </button>
              )}
            </>
          )}

          {/* Aurora IA: abre el panel lateral, igual que en Orvian Docs/Sheets (en el teléfono, desde «Más») */}
          {!movil && (
          <button
            type="button"
            className={`zo-spark-btn ${auroraAbierta ? 'is-on' : ''}`}
            onClick={acciones.onAurora}
            title="Aurora IA"
            aria-label="Aurora IA"
            aria-pressed={!!auroraAbierta}
            data-testid="fe-aurora-toggle"
          >
            <AuroraLogo size={22} />
          </button>
          )}

          {puedeEditar && (
            <button
              type="button"
              className={`fe-btn-publicar ${pub.publicado ? 'is-publicado' : ''}`}
              onClick={acciones.onPublicar}
              title={pub.publicado ? 'Configuración de publicación' : 'Publicar formulario'}
              aria-label={pub.publicado ? 'Configuración de publicación' : 'Publicar formulario'}
            >
              {pub.publicado ? <Globe /> : <Send />}
              {/* En el teléfono solo el icono: el texto empujaba el título y tapaba el «Guardado» */}
              {!movil && <span>{pub.publicado ? 'Publicado' : 'Publicar'}</span>}
            </button>
          )}

          {menuMas}

          {!movil && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="fe-avatar fe-avatar-usuario" style={{ background: colorAvatar(usuario?.email || nombreUsuario) }} title={nombreUsuario} aria-label="Cuenta">
                  {iniciales(nombreUsuario)}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="fe-menu w-64">
                <DropdownMenuLabel className="font-normal">
                  <div className="text-sm font-medium truncate">{nombreUsuario}</div>
                  {usuario?.email && usuario.email !== nombreUsuario && <div className="text-xs opacity-70 truncate">{usuario.email}</div>}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={acciones.onAlternarOscuro}>
                  {oscuro ? <Sun /> : <Moon />} {oscuro ? 'Tema claro' : 'Tema oscuro'}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={acciones.onDrive}><FolderOpen /> Ir a Drive</DropdownMenuItem>
                <DropdownMenuItem onSelect={acciones.onCorreo}><Mail /> Ir al correo</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <nav className="fe-pestanas" role="tablist" aria-label="Secciones del editor">
        {PESTANAS.filter((p) => (p.id !== 'respuestas' || puedeEditar) && (p.id !== 'casos' || (BUZON_CASOS && (puedeEditar || formulario?.buzon?.activo)))).map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={pestana === p.id}
            className={`fe-pestana ${pestana === p.id ? 'is-activa' : ''}`}
            onClick={() => onPestana(p.id)}
          >
            {p.texto}
            {p.id === 'respuestas' && respuestas > 0 && <span className="fe-pestana-contador">{respuestas}</span>}
          </button>
        ))}
      </nav>
    </header>
  );
}
